import { debugTracking } from '$lib/debug';
import { setTrackingProperty } from '$lib/helper';

import get from 'lodash/get';
import set from 'lodash/set';

import events from './events';
import { createMappedFormValues } from './helper';
import { increaseSequentialId } from './increaseSequentialId';
import { getTrackingService } from './TrackingServiceInstance';
import { identify, page, track } from './v3.calls';
import getQueryParameter from '../helper/get-query-parameter';

import type { Field, Answer, Input } from '$lib/types/Field';
import type { MappedFormValues } from '$lib/types/tracking';

const trackFormSubmit = ({
    formId,
    formValues,
    fields,
    inputMap,
}: {
    formId: string;
    formValues: { [key: string]: any };
    fields: Field[];
    inputMap?: any;
}) => {
    debugTracking('-> V3 trackFormSubmit:', formId, formValues, fields, inputMap);

    const formField = fields.find(({ additional }) => {
        return get(additional, 'form.componentId') === formId;
    });

    if (!formField) {
        return;
    }

    const inputFieldList = get(formField, 'additional.form.inputs');

    const mappedFormValues = createMappedFormValues({
        values: formValues,
        formId,
        inputFieldList,
        inputMap,
    });

    inputFieldList.forEach((field: Input) => {
        const existing =
            mappedFormValues.findIndex((mappedFormValue) => {
                return mappedFormValue?.inputField.componentId === field.componentId;
            }) > -1;

        if (!existing) {
            if (field.inputType === 'checkbox') {
                mappedFormValues.push({
                    inputField: field,
                    value: false,
                    conversionTrigger: field.conversionTrigger,
                });
            }
        }
    });

    updateUser(mappedFormValues);
    trackInteractionFormEvent(formField, mappedFormValues);
    trackFormEvent(mappedFormValues);
};

const updateUser = (mappedFormValues: MappedFormValues) => {
    debugTracking('-> V3 updateUser:', mappedFormValues);

    const identifyTraits = {};

    mappedFormValues.forEach((mappedFormValue) => {
        set(identifyTraits, mappedFormValue?.inputField?.fieldName || '', mappedFormValue?.value);
    });

    identify(identifyTraits);
};

const trackFormEvent = (mappedFormValues: MappedFormValues) => {
    debugTracking('-> V3 trackFormEvent:', mappedFormValues);

    const name = events.formEvent;
    const optionsName = events.formEvent;
    const trackingObj: { [key: string]: any } = {};

    mappedFormValues.forEach((mappedFormValue) => {
        if (mappedFormValue) {
            trackingObj[mappedFormValue.inputField.fieldName] = mappedFormValue.value;
        }
    });

    return track(name, trackingObj, optionsName);
};

const trackInteractionFormEvent = (formField: Field, mappedFormValues: MappedFormValues) => {
    debugTracking('-> V3 trackInteractionFormEvent:', formField, mappedFormValues);

    const name = events.interactionFormEvent;
    const optionsName = events.interactionFormEvent;
    const campaignId = formField.campaignId;

    const trackingObj = {
        componentId: formField.componentId, // question form
        fieldId: formField._id,
        optIns: mappedFormValues.map((mappedFormValue) => {
            if (mappedFormValue) {
                setTrackingProperty(
                    mappedFormValue.inputField.fieldName,
                    mappedFormValue.value,
                    campaignId,
                );

                return {
                    componentId: mappedFormValue.inputField.componentId, // input
                    value: mappedFormValue.value,
                    conversionTrigger: mappedFormValue.inputField.conversionTrigger,
                };
            }
        }),
    };

    return track(name, trackingObj, optionsName);
};

const trackMultipleChoiceClick = ({
    answerIds,
    questionId,
    fields,
}: {
    answerIds: string[];
    questionId: string;
    fields: Field[];
}) => {
    debugTracking('-> V3 trackMultipleChoiceClick:', answerIds, questionId, fields);

    const questionField = fields.find(({ componentId }) => {
        return questionId === componentId;
    });

    const answerFields = questionField?.additional.answers.filter(({ componentId }) => {
        return answerIds.indexOf(componentId) > -1;
    });

    const value = answerFields
        ?.map(({ value }) => value)
        ?.sort()
        .join('; ');

    trackInteractionQuestionMultipleChoiceEvent(questionField, answerFields, value);
    trackAnswerEvent(questionField?.title, value);

    const userInformation = {
        [`ps_${questionId}`]: value,
    };

    if (questionField?.title) {
        userInformation[questionField.title] = value;
    }

    identify(userInformation);
};

const trackInteractionQuestionMultipleChoiceEvent = (
    field: Field | undefined,
    answerFields: Answer[] | undefined,
    value: string | undefined,
) => {
    debugTracking('-> V3 trackInteractionQuestionMultipleChoiceEvent:', field, answerFields, value);

    const name = events.interactionQuestionMultipleChoiceEvent;
    const optionsName = events.interactionQuestionMultipleChoiceEvent;

    const trackingObj = {
        componentId: field?.componentId, // question text / question media
        fieldId: field?._id,
        answerIds: answerFields?.map(({ componentId }) => componentId),
        title: field?.title,
        value,
    };

    if (answerFields) {
        setTrackingProperty(answerFields[0].fieldName, value, field?.campaignId);
    }

    return track(name, trackingObj, optionsName);
};

const trackAnswerClick = ({
    answerId,
    questionId,
    fields,
}: {
    answerId?: string;
    questionId: string;
    fields: Field[];
}) => {
    debugTracking('-> V3 trackAnswerClick:', answerId, questionId, fields);

    const questionField = fields.find(({ componentId }) => {
        return questionId === componentId;
    });

    if (!questionField) {
        return;
    }

    const answerField = questionField.additional.answers.find(({ componentId }) => {
        return answerId === componentId;
    });

    if (!answerField) {
        return;
    }

    trackInteractionQuestionEvent(questionField, answerField);
    trackAnswerEvent(questionField.title, answerField.value);
    identify({
        [questionField.title]: answerField.value,
        [`ps_${questionId}`]: answerField.value,
    });
};

const trackInteractionQuestionEvent = (field: Field, answerField: Answer) => {
    debugTracking('-> V3 trackInteractionQuestionEvent:', field, answerField);

    const name = events.interactionQuestionEvent;
    const optionsName = events.interactionQuestionEvent;
    const trackingObj = {
        componentId: field.componentId, // question text / question media
        fieldId: field._id,
        answerId: answerField.componentId,
        title: field.title,
        value: answerField.value,
        visual: answerField.visual,
        visualType: answerField.visualType,
    };

    setTrackingProperty(answerField.fieldName, answerField.value, field.campaignId);

    return track(name, trackingObj, optionsName);
};

// only for 3rd-parties
const trackAnswerEvent = (title: string | undefined, value: string | undefined) => {
    debugTracking('-> V3 trackAnswerEvent:', title, value);

    const name = events.answerEvent;
    const optionsName = events.answerEvent;
    const trackingObj: { [key: string]: string | undefined } = {
        question: title,
        answer: value,
    };

    if (title) {
        trackingObj[title] = value;
    }

    return track(name, trackingObj, optionsName);
};

const trackMeta = (data: any) => {
    debugTracking('-> V3 trackMeta:', data);

    const name = events.metaEvent;
    const optionsName = events.metaEvent;
    const trackingObj = {
        ...data,
    };

    return track(name, trackingObj, optionsName);
};

const trackButtonClick = (id?: string) => {
    debugTracking('-> V3 trackButtonClick:', id);

    const name = id || 'button_click';
    const optionsName = events.buttonEvent;
    const trackingObj = {};

    return track(name, trackingObj, optionsName);
};

const trackImageClick = (id?: string) => {
    debugTracking('-> V3 trackImageClick:', id);

    const name = id || 'image_click';
    const optionsName = events.buttonEvent;
    const trackingObj = {};

    return track(name, trackingObj, optionsName);
};

const trackPage = (
    {
        name,
        tag,
        campaignId,
        slug,
    }: { name: string; tag: string; campaignId: string; slug: string },
    isLastPage: boolean,
) => {
    debugTracking('-> V3 trackPage:', name, tag, campaignId, slug, isLastPage);

    const queryParams = getQueryParameter();
    const trackingObj = {
        ...getTrackingService().getEventContext(),
        utmParams: queryParams.utm,
        custom: queryParams.custom,
        pageType: tag,
        isLastPage,
        sequentialId: increaseSequentialId(),
    };

    trackFacebookPageView({ slug, name });

    return page(name, trackingObj);
};

const trackFacebookPageView = ({ slug, name }: { slug: string; name: string }) => {
    debugTracking('-> V3 trackFacebookPageView:', slug, name);

    const optionsName = events.facebookPageViewEvent;

    return track(
        `PageView: ${slug}`,
        {
            slug,
            name,
        },
        optionsName,
    );
};

const trackCalendly = () => {
    debugTracking('-> V3 trackCalendly');

    const trackingObject = {
        list: [
            {
                fieldName: 'ps_calendly',
                title: 'Calendly Booking',
                value: true,
            },
        ],
    };

    return trackMeta(trackingObject);
};

const trackCal = () => {
    debugTracking('-> V3 trackCal');

    const trackingObject = {
        list: [
            {
                fieldName: 'ps_cal',
                title: 'Cal.com Booking',
                value: true,
            },
        ],
    };

    return trackMeta(trackingObject);
};

const trackGHL = () => {
    debugTracking('-> V3 trackGHL');

    const trackingObject = {
        list: [
            {
                fieldName: 'ps_ghl',
                title: 'GoHighLevel Booking',
                value: true,
            },
        ],
    };

    return trackMeta(trackingObject);
};

const trackHubSpot = () => {
    debugTracking('-> V3 trackHubSpot');

    const trackingObject = {
        list: [
            {
                fieldName: 'ps_hubspot',
                title: 'HubSpot Booking',
                value: true,
            },
        ],
    };

    return trackMeta(trackingObject);
};

const trackIClosed = () => {
    debugTracking('-> V3 trackIClosed');

    const trackingObject = {
        list: [
            {
                fieldName: 'ps_iclosed',
                title: 'iClosed Booking',
                value: true,
            },
        ],
    };

    return trackMeta(trackingObject);
};

const trackCookies = (integrations: string[]) => {
    debugTracking('-> V3 trackCookies');

    const trackingObject = {
        list: [
            {
                fieldName: 'ps_cookies',
                title: 'Cookies accepted',
                value: integrations.sort().join('; '),
            },
        ],
    };

    return trackMeta(trackingObject);
};

const trackVariant = (pageName: string) => {
    debugTracking('-> V3 trackVariant:', pageName);

    const trackingObject = {
        list: [
            {
                fieldName: 'ps_variant',
                title: 'Landing Page',
                value: pageName,
            },
        ],
    };

    return trackMeta(trackingObject);
};

export {
    trackFormSubmit,
    updateUser,
    trackFormEvent,
    trackInteractionFormEvent,
    trackMultipleChoiceClick,
    trackInteractionQuestionMultipleChoiceEvent,
    trackAnswerClick,
    trackInteractionQuestionEvent,
    trackAnswerEvent,
    trackMeta,
    trackButtonClick,
    trackImageClick,
    trackPage,
    trackCalendly,
    trackCal,
    trackGHL,
    trackHubSpot,
    trackIClosed,
    trackCookies,
    trackVariant,
};
